/**
 * Searchanise → Dawn predictive-search bridge
 * -------------------------------------------
 * The Searchanise "Search & Filter" app renders its autocomplete into a
 * position:fixed container (`.snize-ac-results-content`) that conflicts with
 * Dawn's header search drawer. This bridge hides that fixed dropdown (without
 * removing it from the DOM) and mirrors its markup into Dawn's native
 * predictive-search container so the results sit correctly inside the drawer.
 *
 * Design decisions (confirmed with the team):
 *  - Bridge EVERY <predictive-search> instance generically (the theme can render
 *    up to two: Search-In-Modal and Search-In-Modal-1). No hardcoded input IDs.
 *  - Dawn's native predictive search stays ON. The bridge re-asserts the app
 *    output whenever Dawn rewrites the container, guarded against self-loops.
 *  - Visibility reuses Dawn's own open-state: predictive-search[open] .predictive-search
 *    is display:block in component-predictive-search.css, so we just toggle the
 *    `open` attribute on the <predictive-search> element.
 *
 * Purely additive — Dawn's predictive-search.js and search logic are untouched.
 * NOTE: only innerHTML is copied, so JS listeners Searchanise binds to result
 * items are NOT carried over. If result clicks fail to navigate during
 * verification, the fallback is to relocate the live node instead of copying.
 */
(function () {
  'use strict';

  var SOURCE_SELECTOR = '.snize-ac-results-content'; // app output (source)
  var INSTANCE_SELECTOR = 'predictive-search'; // Dawn custom element
  var WRAPPER_SELECTOR = '[data-predictive-search]'; // visible results box
  var RESULTS_ID = 'predictive-search-results'; // Dawn results container
  var HIDE_CLASS = 'snize-bridge-hidden';
  var STYLE_ID = 'snize-bridge-styles';

  var rootObserver = null;
  var isSyncing = false; // self-write guard for the target observers
  var scheduled = false; // rAF debounce flag

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    // Hide the app's own fixed dropdown without removing it from the DOM, so
    // Searchanise keeps populating it and we can keep reading its innerHTML.
    // Class-based (not inline !important) so the app's JS still drives it.
    style.textContent = '.' + HIDE_CLASS + ' { display: none; }';
    document.head.appendChild(style);
  }

  function getInstances() {
    return Array.prototype.slice.call(document.querySelectorAll(INSTANCE_SELECTOR));
  }

  // Searchanise can mount more than one results container (it binds per input,
  // and renders a separate one for the mobile drawer). The bridge must read
  // from whichever node the app actually populated, not just the first.
  function getSourceNodes() {
    return Array.prototype.slice.call(document.querySelectorAll(SOURCE_SELECTOR));
  }

  // The source node to mirror: the first with non-empty content, else the
  // first node at all. On desktop (single node) this resolves identically to
  // the previous querySelector behavior; on mobile it follows the active node.
  function getActiveSource() {
    var nodes = getSourceNodes();
    if (!nodes.length) return null;
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].innerHTML.trim().length) return nodes[i];
    }
    return nodes[0];
  }

  function getInput(instance) {
    return instance ? instance.querySelector('input[type="search"]') : null;
  }

  function getWrapper(instance) {
    return instance ? instance.querySelector(WRAPPER_SELECTOR) : null;
  }

  // The drawer the shopper is actually using: the instance holding the focused
  // element, else the first whose input has a value, else the first instance.
  function getActiveInstance() {
    var instances = getInstances();
    if (!instances.length) return null;

    var active = document.activeElement;
    if (active) {
      for (var i = 0; i < instances.length; i++) {
        if (instances[i].contains(active)) return instances[i];
      }
    }
    for (var j = 0; j < instances.length; j++) {
      var input = getInput(instances[j]);
      if (input && input.value.trim().length) return instances[j];
    }
    return instances[0];
  }

  // Find (or create) the Dawn results container inside an instance's wrapper.
  // Scoped to the instance so duplicate IDs across the two drawers don't clash.
  function getResultsContainer(instance, create) {
    var wrapper = getWrapper(instance);
    if (!wrapper) return null;

    var container = wrapper.querySelector('#' + RESULTS_ID);
    if (!container && create) {
      container = document.createElement('div');
      container.id = RESULTS_ID;
      container.setAttribute('role', 'listbox');
      // Sit at the top of the wrapper, mirroring Dawn's own markup order.
      wrapper.insertBefore(container, wrapper.firstChild);
    }
    return container;
  }

  // Reuse Dawn's open-state attribute so visibility follows the theme CSS.
  function openInstance(instance) {
    if (instance) instance.setAttribute('open', 'true');
  }

  function closeInstance(instance) {
    if (instance) instance.removeAttribute('open');
  }

  function clearInstance(instance) {
    var container = getResultsContainer(instance, false);
    if (container) container.innerHTML = '';
    closeInstance(instance);
  }

  function sync() {
    var sourceNode = getActiveSource();

    var instance = getActiveInstance();
    if (!instance) return;

    var input = getInput(instance);
    var hasQuery = !!(input && input.value.trim().length);
    var appHTML = sourceNode ? sourceNode.innerHTML.trim() : '';

    // Empty query or no app output → clear this drawer and hide it.
    if (!hasQuery || !appHTML) {
      clearInstance(instance);
      return;
    }

    var container = getResultsContainer(instance, true);
    if (!container) return;

    isSyncing = true;
    container.innerHTML = appHTML;
    container.setAttribute('data-snize-bridged', 'true');
    openInstance(instance);
    // Release the guard after the DOM settles so our own mutation doesn't
    // retrigger the target observer (MutationObserver microtasks fire before
    // this rAF, so they still see isSyncing === true and bail).
    window.requestAnimationFrame(function () {
      isSyncing = false;
    });
  }

  function scheduleSync() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      sync();
    });
  }

  // Attach a mirroring observer to every Searchanise results container that
  // isn't already observed. Guarded per node so re-runs never double-bind.
  // Returns true once at least one node exists (current or previously bound).
  function observeSources() {
    var nodes = getSourceNodes();
    var found = false;

    nodes.forEach(function (node) {
      found = true;
      if (node.getAttribute('data-snize-observed') === 'true') return;

      node.setAttribute('data-snize-observed', 'true');
      node.classList.add(HIDE_CLASS);

      var observer = new MutationObserver(scheduleSync);
      observer.observe(node, { childList: true, subtree: true });
    });

    if (found) scheduleSync();
    return found;
  }

  function bindInstances() {
    getInstances().forEach(function (instance) {
      // Clear/hide the drawer the moment the input is emptied.
      var input = getInput(instance);
      if (input && input.getAttribute('data-snize-bound') !== 'true') {
        input.setAttribute('data-snize-bound', 'true');
        input.addEventListener('input', function () {
          if (!input.value.trim().length) {
            clearInstance(instance);
          } else {
            scheduleSync();
          }
        });
      }

      // "Let the bridge win": if Dawn's native fetch rewrites the container,
      // re-assert the app output. isSyncing prevents our own writes looping.
      var wrapper = getWrapper(instance);
      if (wrapper && wrapper.getAttribute('data-snize-target-observed') !== 'true') {
        wrapper.setAttribute('data-snize-target-observed', 'true');
        var targetObserver = new MutationObserver(function () {
          if (isSyncing) return;
          var container = wrapper.querySelector('#' + RESULTS_ID);
          if (!container || container.getAttribute('data-snize-bridged') !== 'true') {
            scheduleSync();
          }
        });
        targetObserver.observe(wrapper, { childList: true, subtree: true });
      }
    });
  }

  function init() {
    injectStyles();
    bindInstances();
    observeSources();

    // Searchanise loads asynchronously and (on mobile) can mount additional
    // results containers later — e.g. a separate one for the mobile drawer
    // created on first focus/keystroke. Keep watching the DOM for the life of
    // the page so every source node (and any re-rendered drawer instance) gets
    // bound. observeSources()/bindInstances() are idempotent via data- guards,
    // so this never double-binds; we deliberately do NOT disconnect here.
    rootObserver = new MutationObserver(function () {
      bindInstances();
      observeSources();
    });
    rootObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
